import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Save, RefreshCw } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { getPrefaceContent, updatePrefaceContent } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";

const FIELDS = [
  { key: "bubbleText", label: "Texte de la bulle de discussion", hint: "Phrase affichée dans la bulle près du robot (accueil)." },
  { key: "cubeMigros", label: "Cube rouge — Mes courses", hint: "Nom affiché dans le bandeau rouge." },
  { key: "cubeResto", label: "Cube jaune — Restaurants et boissons", hint: "Nom affiché dans le bandeau jaune." },
  { key: "cubeKiosk", label: "Cube bleu — Kiosk et bazar", hint: "Nom affiché dans le bandeau bleu." },
  { key: "cubePharma", label: "Cube vert — Soins et services", hint: "Nom affiché dans le bandeau vert." },
];

export default function FounderPreface() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getPrefaceContent()
      .then((c) => setForm(c))
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const save = async () => {
    setSaving(true);
    try {
      const { content } = await updatePrefaceContent(form);
      setForm(content);
      toast.success("Préface mise à jour");
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="fp-denied">
        <p style={{ color: "#64748b" }}>Édition réservée au Founder (admin).</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="founder-preface">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <LayoutDashboard size={22} color={ORANGE} />
          <h1 className="text-2xl font-extrabold" style={{ color: "#0b1220" }}>Contenu de la Préface</h1>
        </div>
        <button data-testid="fp-refresh" onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-sm font-bold px-3 h-10 rounded-xl text-white" style={{ background: GREEN, opacity: loading ? 0.6 : 1 }}>
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Recharger
        </button>
      </div>

      {loading || !form ? (
        <div className="h-64 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="fp-loading" />
      ) : (
        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="rounded-2xl bg-white p-4" style={{ border: "1px solid #eef0ef" }}>
              <label className="block text-sm font-bold mb-1" style={{ color: "#0b1220" }}>{f.label}</label>
              <p className="text-xs mb-2" style={{ color: "#94a3b8" }}>{f.hint}</p>
              <input data-testid={`fp-input-${f.key}`} value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full rounded-xl px-3 py-2 text-sm" style={{ border: "1px solid #d7dedb", outlineColor: GREEN }} />
            </div>
          ))}

          <button data-testid="fp-save" onClick={save} disabled={saving}
            className="flex items-center justify-center gap-2 w-full rounded-2xl font-extrabold text-white px-5 py-3.5"
            style={{ background: ORANGE, opacity: saving ? 0.6 : 1 }}>
            <Save size={18} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      )}
    </div>
  );
}
